/**
 * Vehicle Filter - Hampton Tuning
 * Fetches products matching selected vehicle and displays grouped by category.
 * Uses Shopify AJAX API with tag-based filtering.
 *
 * Tag format on products: fit:Make|Model|Series
 * Example: fit:Audi|RS3|8V (2017-2020)
 */
(function () {
  'use strict';

  const RESULTS_CONTAINER = document.getElementById('vehicle-results-content');
  if (!RESULTS_CONTAINER) return;

  const params = new URLSearchParams(window.location.search);
  const make = params.get('make');
  const model = params.get('model');
  const series = params.get('series');

  if (!make || !model) {
    RESULTS_CONTAINER.innerHTML = '<div class="vehicle-results__empty">Please select a vehicle using the search above.</div>';
    return;
  }

  const fitTag = series ? `fit:${make}|${model}|${series}` : `fit:${make}|${model}`;

  const headerEl = document.getElementById('vehicle-results-header');
  if (headerEl) {
    const label = series ? `${make} ${model} ${series}` : `${make} ${model}`;
    headerEl.querySelector('.vehicle-results__title').innerHTML = `Parts for <span>${label}</span>`;
    headerEl.style.display = '';
  }

  RESULTS_CONTAINER.innerHTML = '<div class="vehicle-results__loading"><div class="spinner"></div><br>Finding parts for your vehicle...</div>';

  fetchAllProducts(fitTag)
    .then(products => {
      if (products.length === 0) {
        RESULTS_CONTAINER.innerHTML = '<div class="vehicle-results__empty">No parts found for this vehicle yet. Check back soon!</div>';
        return;
      }
      renderGroupedProducts(products);
    })
    .catch(err => {
      console.error('Vehicle filter error:', err);
      RESULTS_CONTAINER.innerHTML = '<div class="vehicle-results__empty">Something went wrong. Please try again.</div>';
    });

  async function fetchAllProducts(tag) {
    const allProducts = [];
    let page = 1;
    const limit = 250;
    const encodedTag = encodeURIComponent(tag);

    while (true) {
      try {
        const url = `/collections/all/${encodedTag}.json?page=${page}&limit=${limit}`;
        const resp = await fetch(url);
        if (!resp.ok) return await fetchAndFilterProducts(tag);
        const data = await resp.json();
        const products = data.products || [];
        if (products.length === 0) break;
        allProducts.push(...products);
        if (products.length < limit) break;
        page++;
      } catch (e) {
        return await fetchAndFilterProducts(tag);
      }
    }
    return allProducts;
  }

  async function fetchAndFilterProducts(tag) {
    const allProducts = [];
    let page = 1;
    const limit = 250;

    while (true) {
      const url = `/products.json?page=${page}&limit=${limit}`;
      const resp = await fetch(url);
      if (!resp.ok) break;
      const data = await resp.json();
      const products = data.products || [];
      if (products.length === 0) break;
      const matching = products.filter(p => {
        if (!p.tags) return false;
        const tagList = Array.isArray(p.tags) ? p.tags : p.tags.split(', ');
        return tagList.some(t => (typeof t === 'string' ? t.trim() : t) === tag);
      });
      allProducts.push(...matching);
      if (products.length < limit) break;
      page++;
    }
    return allProducts;
  }

  /**
   * Priority order for categories.
   */
  const CATEGORY_PRIORITY = [
    'Turbo Kits', 'Exhaust', 'Induction', 'Intercoolers', 'Fueling',
    'Tuning', 'Body Kits', 'Catch Cans', 'Valves', 'Hardpipes',
    'Car Hose Kits', 'Muffler Deletes', 'Brake Lines', 'Transmission',
    'Steering Wheels', 'Chargecooler Radiators', 'Oil Cooler', 'Radiators',
    'Engine Internals', 'Actuators', 'Turbo Blankets', 'Blow Off Adaptors',
    'Boost Gauge Fitting Kits', 'Wheel Spacers', 'Tie Bars',
    'Water/Methanol Injection', 'Gauges', 'Ignition', 'Lighting',
    'Interior Trim', 'Maintenance', 'Power Bundles',
  ];

  /**
   * Group products by category, with "On Sale" items pulled out separately.
   */
  function renderGroupedProducts(products) {
    const groups = {};
    const uncategorized = [];
    const onSale = [];

    products.forEach(product => {
      const category = product.product_type || '';

      // Check if product is on sale (has compare_at_price > price)
      const variant = product.variants[0];
      const price = parseFloat(variant.price);
      const compareAt = parseFloat(variant.compare_at_price);
      const isOnSale = compareAt && compareAt > price;

      if (isOnSale) {
        onSale.push(product);
      }

      // Also add to regular category (product shows in both)
      if (category) {
        if (!groups[category]) groups[category] = [];
        groups[category].push(product);
      } else {
        uncategorized.push(product);
      }
    });

    const allCategories = Object.keys(groups);
    const sortedCategories = allCategories.sort((a, b) => {
      const aIdx = CATEGORY_PRIORITY.indexOf(a);
      const bIdx = CATEGORY_PRIORITY.indexOf(b);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return a.localeCompare(b);
    });

    let html = '';

    // Render "On Sale" section first if there are sale items
    if (onSale.length > 0) {
      html += renderSaleCategory('On Sale', onSale);
    }

    sortedCategories.forEach(category => {
      html += renderCategory(category, groups[category]);
    });

    if (uncategorized.length > 0) {
      html += renderCategory('Other Parts', uncategorized);
    }

    RESULTS_CONTAINER.innerHTML = html;
  }

  /**
   * Render the "On Sale" section with special styling.
   */
  function renderSaleCategory(title, products) {
    let html = `<div class="vehicle-results__category vehicle-results__category--sale">`;
    html += `<h3 class="vehicle-results__category-title" style="color: #e53935; display: flex; align-items: center; gap: 8px;">`;
    html += `<svg style="width:22px;height:22px;fill:#e53935;" viewBox="0 0 24 24"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg>`;
    html += `${escapeHtml(title)} <span style="font-size: 13px; font-weight: 400; color: #888;">(${products.length} items)</span>`;
    html += `</h3>`;
    html += `<div class="vehicle-results__grid">`;

    products.forEach(product => {
      const image = product.images && product.images.length > 0
        ? product.images[0].src
        : 'https://cdn.shopify.com/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif';

      const price = formatMoney(product.variants[0].price);
      const comparePrice = product.variants[0].compare_at_price
        ? `<span class="compare" style="text-decoration: line-through; color: #999; margin-left: 6px;">${formatMoney(product.variants[0].compare_at_price)}</span>`
        : '';

      const savings = product.variants[0].compare_at_price
        ? Math.round((1 - parseFloat(product.variants[0].price) / parseFloat(product.variants[0].compare_at_price)) * 100)
        : 0;
      const savingsBadge = savings > 0
        ? `<span style="background: #e53935; color: #fff; font-size: 11px; font-weight: 700; padding: 2px 6px; border-radius: 3px; margin-left: 6px;">-${savings}%</span>`
        : '';

      html += `
        <div class="vehicle-results__product">
          <a href="/products/${product.handle}">
            <div style="position:relative;">
              <img class="vehicle-results__product-image" src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">
              ${savings > 0 ? `<span style="position:absolute;top:8px;left:8px;background:#e53935;color:#fff;font-size:11px;font-weight:700;padding:3px 8px;border-radius:4px;">SALE</span>` : ''}
            </div>
            <div class="vehicle-results__product-info">
              <h4 class="vehicle-results__product-title">${escapeHtml(product.title)}</h4>
              <div class="vehicle-results__product-price" style="color: #e53935; font-weight: 600;">${price} ${comparePrice} ${savingsBadge}</div>
            </div>
          </a>
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  }

  /**
   * Render a regular category section.
   */
  function renderCategory(title, products) {
    let html = `<div class="vehicle-results__category">`;
    html += `<h3 class="vehicle-results__category-title">${escapeHtml(title)}</h3>`;
    html += `<div class="vehicle-results__grid">`;

    products.forEach(product => {
      const image = product.images && product.images.length > 0
        ? product.images[0].src
        : 'https://cdn.shopify.com/shopifycloud/shopify/assets/no-image-2048-5e88c1b20e087fb7bbe9a3771824e743c244f437e4f8ba93bbf7b11b53f7824c.gif';

      const price = formatMoney(product.variants[0].price);
      const comparePrice = product.variants[0].compare_at_price
        ? `<span class="compare" style="text-decoration: line-through; color: #999; margin-left: 6px;">${formatMoney(product.variants[0].compare_at_price)}</span>`
        : '';

      html += `
        <div class="vehicle-results__product">
          <a href="/products/${product.handle}">
            <img class="vehicle-results__product-image" src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">
            <div class="vehicle-results__product-info">
              <h4 class="vehicle-results__product-title">${escapeHtml(product.title)}</h4>
              <div class="vehicle-results__product-price">${price} ${comparePrice}</div>
            </div>
          </a>
        </div>
      `;
    });

    html += `</div></div>`;
    return html;
  }

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseFloat(cents);
    return '$' + (cents / 1).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
})();